/**
 * GenerationFormActions
 *
 * Form actions: cost display, submit button, templates link.
 */
import { Wand2, ExternalLink, AlertTriangle, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button, Badge, Alert, AlertDescription } from '@/components/v2/ui';
import { formatNumber } from '@/lib/utils';

interface GenerationFormActionsProps {
  isSubmitting: boolean;
  canSubmit: boolean;
  credits: number;
  cost: number;
  hasEnoughCredits: boolean;
  canUseCustomPresets: boolean;
  hasGeneratedImage?: boolean;
  onSubmit?: () => void;
}

export function GenerationFormActions({
  isSubmitting,
  canSubmit,
  cost,
  hasEnoughCredits,
  canUseCustomPresets,
  hasGeneratedImage = false,
}: GenerationFormActionsProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      {/* Cost Display */}
      {cost > 0 && (
        <div className="flex items-center justify-between p-3 rounded-lg bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border border-amber-200 dark:border-amber-800">
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
              {t('generation.costLabel')}
            </span>
          </div>
          <Badge variant={hasEnoughCredits ? 'default' : 'error'} className="font-bold">
            {formatNumber(cost)} Credits
          </Badge>
        </div>
      )}

      {/* Insufficient Credits Warning */}
      {!hasEnoughCredits && cost > 0 && (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>{t('generation.notEnoughCredits')}</strong>{' '}
            <a href="/v2/credits" className="underline font-medium hover:text-amber-700 dark:hover:text-amber-300">
              {t('billing.bookNow')}
            </a>
          </AlertDescription>
        </Alert>
      )}

      {/* Submit Button */}
      <Button
        type="submit"
        variant="primary"
        className="w-full"
        disabled={!canSubmit}
        loading={isSubmitting}
      >
        {isSubmitting ? (
          t('common.generating')
        ) : (
          <>
            <Wand2 className="h-4 w-4 mr-2" />
            {hasGeneratedImage ? t('common.generate') : t('common.generate')}
          </>
        )}
      </Button>

      {/* Templates Link */}
      {canUseCustomPresets && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="w-full"
          onClick={() => window.location.href = '/v2/custom-presets'}
        >
          <ExternalLink className="h-3 w-3 mr-1" />
          {t('templates.myTemplates')}
        </Button>
      )}
    </div>
  );
}
